// Shared fetch wrapper for all API modules (authService, studentService).
//
// Security contract (see CLAUDE.md):
// - `credentials: "include"` → Django's httpOnly session cookie rides along.
// - `X-CSRF-Token` header read from the JS-visible `XSRF-TOKEN` cookie
//   (double-submit pattern) on every mutating request.
// - Auth state lives ONLY in the session cookie — never in local/sessionStorage.

import i18n from "i18next";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

// In dev the CRA proxy only forwards XHR-style requests, not top-level
// navigations — so links to uploaded files (/media/...) must target Django
// directly. In production nginx serves /media on the same origin.
export const MEDIA_BASE_URL =
  process.env.NODE_ENV === "development" ? "http://localhost:8000" : "";

export class ApiError extends Error {
  constructor(message, { status, errors } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors || {};
  }
}

function readCsrfCookie() {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
}

// The backend answers in the language the user is currently reading the site
// in (apps/localization.py); i18next is the source of truth for that.
function readLanguage() {
  return (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
}

function formatErrorMessage(data) {
  if (data.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors).filter(Boolean);
    if (messages.length) return messages.join(" ");
  }
  return data.message || "Something went wrong. Please try again.";
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// GETs are idempotent, so transient failures (dropped proxy socket in dev,
// brief network blip, 502 during a deploy) are retried transparently instead
// of surfacing as a scary error on first page load.
const GET_RETRIES = 2;
// 500 is included because the CRA dev proxy reports "backend unreachable"
// as a 500 — retrying a GET is safe either way (idempotent).
const RETRYABLE_STATUSES = new Set([500, 502, 503, 504]);

/**
 * request("/auth/signin/", { method: "POST", body: {...} })
 * - `body` as a plain object is JSON-encoded; a FormData instance is sent as-is
 *   (multipart, for file uploads).
 * - Throws ApiError with `.status` and per-field `.errors` on non-2xx.
 */
export async function request(path, { method = "GET", body, cache } = {}) {
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = { "X-Language": readLanguage() };
  if (method !== "GET") headers["X-CSRF-Token"] = readCsrfCookie();
  if (body !== undefined && !isFormData) headers["Content-Type"] = "application/json";

  const attempts = method === "GET" ? GET_RETRIES + 1 : 1;

  for (let attempt = 1; ; attempt++) {
    let response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        credentials: "include",
        headers,
        cache,
        body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      });
    } catch (networkError) {
      // fetch() itself rejected — no response at all (connection refused/reset).
      if (attempt < attempts) {
        await sleep(400 * attempt);
        continue;
      }
      throw new ApiError("Can't reach the server. Check your connection and try again.", { status: 0 });
    }

    if (RETRYABLE_STATUSES.has(response.status) && attempt < attempts) {
      await sleep(400 * attempt);
      continue;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new ApiError(formatErrorMessage(data), { status: response.status, errors: data.errors });
    }

    return data;
  }
}
